/**
 * **Auto-Boost on a move** (`projects/move-auto-boost.md`): a PLAY move to a destination past the
 * move left but inside a Boost's reach Boosts first and then moves, as one gesture. This decides
 * which, and the room's `request_move` referee, the browser's dispatch dry run and the drag forecast
 * all read it, so the three agree on where a Boost is folded and why one is refused.
 *
 * Nothing is re-derived: reach is {@link movePathAnchors} before and after the real `boost`
 * reducer, and a refusal is the reducer's own {@link boostBlocker}.
 */
import { boostBlocker, type BoostBlocker } from "./handlers/budget";
import { movePathCost } from "@lancer-vtt/space";
import { checkMovePath, defaultRouteContext, moveBudgetFor, movePathAnchors } from "./movement";
import { applyEncounter } from "./reducer";
import type { Cell, Combatant, EncounterState } from "./types";
import { worldFor } from "./world";

export type MoveBoostFold =
  /** The move stands or falls on its own: reachable without a Boost, or not even with one. */
  | { kind: "none" }
  /** Boost first. `state` is what the `boost` reducer leaves; `free` when it cost no action (a free-Boost earmark). */
  | { kind: "boost"; state: EncounterState; free: boolean }
  /**
   * The Boost is allowed but its own reach falls short. Only a Boost rider (an `on_action_taken`
   * grant this package can't run) could reach the destination; the room's referee checks those.
   */
  | { kind: "short" }
  /** Only a Boost reaches the destination, and the reducer refuses the Boost. */
  | { kind: "refused"; blocker: BoostBlocker };

const NONE: MoveBoostFold = { kind: "none" };
const SHORT: MoveBoostFold = { kind: "short" };

const actionsLeft = (state: EncounterState): number =>
  state.activation.turn.phase === "acting" ? state.activation.turn.budget.actions : 0;

/**
 * Whether the move reaches its destination on `state`'s budget: along the chosen `path` when the
 * move carries one (its own cost against the move left), else along the engine's route.
 */
export function moveReaches(state: EncounterState, mover: Combatant, move: { to: Cell; path?: readonly Cell[] }): boolean {
  if (move.path === undefined) return movePathAnchors(state, mover, move.to) !== null;
  const walked = checkMovePath(state, mover, move.path);
  return walked.ok && walked.value.cost <= moveBudgetFor(state, mover);
}

export function moveBoostFold(state: EncounterState, move: { moverId: string; to: Cell; actorId: string; path?: readonly Cell[] }): MoveBoostFold {
  const mover = state.combatants[move.moverId];
  if (mover?.pos == null || moveReaches(state, mover, move)) return NONE;
  const boosted = applyEncounter(state, { type: "boost", actorId: move.actorId, combatantId: move.moverId });
  if (boosted.ok) {
    const after = boosted.value.state;
    if (!moveReaches(after, after.combatants[move.moverId]!, move)) return SHORT;
    return { kind: "boost", state: after, free: actionsLeft(after) === actionsLeft(state) };
  }
  // Off-turn or someone else's turn: the move itself is refused, for that reason, not for the Boost.
  const blocker = boostBlocker(state, mover);
  if (blocker === null || blocker.kind === "no_turn" || blocker.kind === "not_actor") return NONE;
  // Would the Boost reach it if it were allowed? `grant_move` is Boost's arithmetic without its gates.
  const granted = applyEncounter(state, { type: "grant_move", actorId: move.actorId, combatantId: move.moverId, cells: mover.base.speed });
  if (!granted.ok || !moveReaches(granted.value.state, granted.value.state.combatants[move.moverId]!, move)) return NONE;
  return { kind: "refused", blocker };
}

/** The rejection a refused fold carries, on the room's `request_move` and the browser's dry run alike. */
export const moveBoostRefusal = (blocker: BoostBlocker): string => `move: only a Boost reaches there, and ${blocker.message}`;

/**
 * The drag trail for a drop: the footprint cells the route sweeps, split where the move left runs
 * out. `cells` is the part the move left pays for; `boostCells` is the rest, which only the folded
 * Boost pays for (empty when no Boost is folded). The route is `movePathAnchors` on the state the
 * fold leaves ({@link moveBoostFold}, the decision the drop's dispatch and the room apply), so the
 * trail covers exactly the drops that commit: `null` when the drop is illegal even after a Boost, or
 * only a Boost reaches it and the reducer refuses the Boost.
 */
export type MovePathPreview = { cells: Set<string>; boostCells: Set<string> };

export function movePathPreview(state: EncounterState, mover: Combatant, dest: Cell): MovePathPreview | null {
  const fold = moveBoostFold(state, { moverId: mover.id, to: dest, actorId: mover.id });
  if (fold.kind === "refused") return null;
  const routed = fold.kind === "boost" ? fold.state : state;
  const unit = routed.combatants[mover.id] ?? mover;
  const path = movePathAnchors(routed, unit, dest);
  if (path === null) return null;
  const world = worldFor(routed);
  // The cost of each prefix of the route, with no engagement stop and no endpoint rule: a prefix
  // may end mid-route on an ally it passes through.
  const ctx = { ...defaultRouteContext(world, routed, unit), stopsAt: undefined, noStop: undefined };
  const costTo = (i: number): number => {
    const r = movePathCost({ space: world, path: path.slice(0, i + 1), size: unit.base.size, ctx });
    return r.ok ? r.cost : Number.POSITIVE_INFINITY;
  };
  const moveLeft = fold.kind === "boost" ? moveBudgetFor(state, mover) : Number.POSITIVE_INFINITY;
  const cells = new Set<string>();
  const boostCells = new Set<string>();
  path.forEach((anchor, i) => {
    const into = costTo(i) <= moveLeft ? cells : boostCells;
    for (const cell of world.footprint(anchor, unit.base.size).cells()) into.add(world.key(cell));
  });
  for (const key of cells) boostCells.delete(key);
  return { cells, boostCells };
}
